import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageSquareText, ArrowRight, Home as HomeIcon, Car, Layers, Footprints, Sparkles, Building2, ShieldCheck, HeartPulse, Leaf, Star } from "lucide-react";
import { BRAND, VIDEOS, SERVICES, MANIFESTO, HEALTH_POINTS, FLYERS } from "@/data/site";
import { MaskedLine, Reveal, ChapterLabel } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Specials from "@/components/Specials";

const SERVICE_ICONS = [HomeIcon, Car, Layers, Footprints, Sparkles, Building2];
const HEALTH_ICONS = [ShieldCheck, HeartPulse, Star, Leaf];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden grain-overlay" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <video
          className="w-full h-full object-cover"
          src={VIDEOS[0].url}
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-background-video"
        />
      </motion.div>
      <div className="absolute inset-0 hero-vignette" />

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-40 w-full">
        <MaskedLine delay={0.15}>
          <span className="font-mono-brand text-xs sm:text-sm tracking-[0.35em] uppercase text-cyan-400 font-bold">
            Pressure Washing & Surface Cleaning
          </span>
        </MaskedLine>
        <h1 className="font-display font-black italic uppercase tracking-tighter leading-[0.9] mt-6 text-5xl sm:text-7xl lg:text-8xl" data-testid="hero-title">
          <MaskedLine delay={0.3}>Your Surface.</MaskedLine>
          <MaskedLine delay={0.45} className="text-cyan-400">My Purpose.</MaskedLine>
        </h1>
        <MaskedLine delay={0.65}>
          <p className="mt-6 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
            {BRAND.tagline} Homes, driveways, decks, fences, sidewalks and more — we clean it all.
          </p>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="hero-call-button"
            className="flex items-center gap-2 bg-cyan-400 text-[#030712] font-bold px-7 py-4 rounded-full hover:bg-cyan-300 hover:scale-105 transition-transform"
          >
            <Phone className="w-5 h-5" /> Call {BRAND.phone}
          </a>
          <a
            href={`sms:${BRAND.phoneRaw}`}
            data-testid="hero-text-button"
            className="flex items-center gap-2 border border-cyan-400/50 text-cyan-300 font-bold px-7 py-4 rounded-full hover:bg-cyan-400/10 transition-colors"
          >
            <MessageSquareText className="w-5 h-5" /> Text Us
          </a>
          <Link
            to="/contact"
            data-testid="hero-estimate-link"
            className="group flex items-center gap-2 text-slate-300 font-semibold hover:text-cyan-400 transition-colors"
          >
            Free Estimate <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="relative py-28 sm:py-36" data-testid="services-preview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <ChapterLabel number="01" text="What We Clean" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl lg:text-7xl">
            We Clean <span className="text-cyan-400 italic">It All</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = SERVICE_ICONS[i];
            return (
              <Reveal key={s.id} delay={i * 0.08}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group relative block bg-[#0b1838] border border-cyan-400/15 rounded-2xl p-8 overflow-hidden hover:border-cyan-400/50 transition-colors"
                >
                  <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-cyan-400/5 group-hover:bg-cyan-400/15 group-hover:scale-150 transition-transform duration-700" />
                  <Icon className="w-9 h-9 text-cyan-400 mb-6" />
                  <p className="font-mono-brand text-[10px] tracking-[0.3em] uppercase text-cyan-400/70 mb-2">{s.tag}</p>
                  <h3 className="font-display font-bold uppercase text-2xl tracking-tight">{s.name}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.short}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-cyan-400 text-xs font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="relative py-28 sm:py-36 bg-[#060f26] border-y border-cyan-400/10" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <ChapterLabel number="02" text="Why It Matters" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
            More Than Clean. <span className="text-cyan-400 italic">It's Protection.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-slate-400 leading-relaxed">
            Mold, mildew, and grime don't just look bad — they can harm your home and your family.
          </p>
        </Reveal>

        <div className="mt-20 relative">
          <div className="absolute left-[19px] sm:left-[27px] top-0 bottom-0 w-px bg-gradient-to-b from-cyan-400/60 via-cyan-400/20 to-transparent" />
          <div className="flex flex-col gap-16">
            {MANIFESTO.map((ch, i) => (
              <Reveal key={ch.number} delay={i * 0.1}>
                <div className="flex gap-8 sm:gap-12 items-start" data-testid={`manifesto-chapter-${ch.number}`}>
                  <span className="relative z-10 font-display font-black italic text-4xl sm:text-5xl text-cyan-400 bg-[#060f26] leading-none shrink-0">
                    {ch.number}
                  </span>
                  <div>
                    <h3 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight">{ch.title}</h3>
                    <p className="mt-3 max-w-2xl text-slate-400 leading-relaxed">{ch.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Showcase() {
  return (
    <section className="relative py-28 sm:py-36 overflow-hidden" data-testid="showcase-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <ChapterLabel number="03" text="Proof In The Wash" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl lg:text-7xl">
            Before. <span className="text-cyan-400 italic">After.</span> Gone.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {[FLYERS.main, FLYERS.services].map((src, i) => (
            <Reveal key={src} delay={i * 0.12}>
              <motion.div
                whileHover={{ scale: 1.02, rotate: i === 0 ? -0.6 : 0.6 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="clip-frame border border-cyan-400/20 shadow-[0_0_60px_rgba(0,240,255,0.08)] overflow-hidden"
                data-testid={`showcase-flyer-${i + 1}`}
              >
                <img src={src} alt={`Your Surface My Purpose results ${i + 1}`} className="w-full h-auto" loading="lazy" />
              </motion.div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-14 text-center">
          <Link
            to="/gallery"
            data-testid="showcase-gallery-link"
            className="group inline-flex items-center gap-3 border border-cyan-400/50 text-cyan-300 font-bold px-8 py-4 rounded-full hover:bg-cyan-400/10 transition-colors"
          >
            Watch The Work In Motion <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Health() {
  return (
    <section className="relative py-28 sm:py-36 bg-[#060f26] border-y border-cyan-400/10" data-testid="health-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <ChapterLabel number="04" text="Health & Home" />
          <h2 className="font-display font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl lg:text-7xl max-w-4xl">
            It's Not Just Dirt. <span className="text-cyan-400 italic">It's A Health Risk.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {HEALTH_POINTS.map((p, i) => {
            const Icon = HEALTH_ICONS[i];
            return (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="h-full bg-[#0b1838] border border-cyan-400/15 rounded-2xl p-7 hover:border-cyan-400/50 transition-colors" data-testid={`health-card-${i + 1}`}>
                  <div className="w-12 h-12 rounded-full border border-cyan-400/40 bg-cyan-400/10 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h3 className="font-display font-bold uppercase text-xl tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="relative py-28 sm:py-36" data-testid="cta-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal>
          <p className="font-mono-brand text-xs tracking-[0.35em] uppercase text-cyan-400 font-bold">Ready For A Cleaner Tomorrow?</p>
          <h2 className="mt-6 font-display font-black italic uppercase tracking-tighter leading-[0.9] text-5xl sm:text-7xl">
            Let Us Wash Your <span className="text-cyan-400">Worries Away</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a href={`tel:${BRAND.phoneRaw}`} data-testid="cta-call-button" className="flex items-center gap-2 bg-cyan-400 text-[#030712] font-bold px-8 py-4 rounded-full hover:bg-cyan-300 hover:scale-105 transition-transform">
              <Phone className="w-5 h-5" /> {BRAND.phone}
            </a>
            <Link to="/contact" data-testid="cta-estimate-button" className="flex items-center gap-2 border border-cyan-400/50 text-cyan-300 font-bold px-8 py-4 rounded-full hover:bg-cyan-400/10 transition-colors">
              Get A Free Estimate <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Specials />
      <ServicesPreview />
      <Manifesto />
      <Showcase />
      <Health />
      <CtaBanner />
    </main>
  );
}
